// Sentry is dynamically imported on first user interaction (or after an idle
// fallback) to keep ~186 KB out of the critical bundle and dramatically improve
// First Contentful Paint. See src/integrations/monitoring/lazyInit.ts.

declare const __APP_VERSION__: string;

const SENTRY_DSN = "https://c5309e5371715e4e8451ef129e63b605@o4510882373959680.ingest.us.sentry.io/4510882403516416";

// Patterns we silently drop — known browser/runtime noise that does not
// represent an actionable bug.
const IGNORED_ERROR_PATTERNS = [
  /aborterror/i,
  /signal is aborted without reason/i,
  /failed to register a serviceworker/i,
  /an unknown error occurred when fetching the script/i,
  /java object is gone/i,
  /enableDidUserTypeOnKeyboardLogging/i,
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk/i,
  /NetworkError when attempting to fetch resource/i,
  /Failed to fetch dynamically imported module/i,
  /The operation was aborted/i,
  /cancelled/i,
];

// URL patterns we drop — third-party scripts we cannot fix.
const DENY_URLS = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /analytics\.tiktok\.com/i,
];

let initPromise: Promise<void> | null = null;
let SentryRef: typeof import("@sentry/react") | null = null;

export const initSentry = () => {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const Sentry = await import("@sentry/react");
    SentryRef = Sentry;

    const extractEventMessages = (event: any, hint?: any): string[] => {
      const exceptionMessages =
        event.exception?.values?.flatMap((value: any) => [value.type, value.value].filter(Boolean) as string[]) ?? [];
      const originalException = hint?.originalException;
      const originalExceptionMessages =
        originalException instanceof Error
          ? [originalException.name, originalException.message]
          : typeof originalException === "string"
            ? [originalException]
            : [];
      return [
        event.message,
        event.logentry?.message,
        ...exceptionMessages,
        ...originalExceptionMessages,
      ].filter((m): m is string => Boolean(m));
    };

    const shouldIgnoreEvent = (event: any, hint?: any) => {
      const messages = extractEventMessages(event, hint);
      return messages.some((message) => IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message)));
    };

    const env =
      (typeof window !== "undefined" && window.location.hostname.includes("lovableproject.com"))
        ? "preview"
        : (typeof window !== "undefined" && /lovable\.app$/.test(window.location.hostname))
          ? "staging"
          : import.meta.env["MODE"] === "production"
            ? "production"
            : "development";

    Sentry.init({
      dsn: SENTRY_DSN,
      release: `amorette@${__APP_VERSION__}`,
      environment: env,
      integrations: [
        Sentry.browserTracingIntegration({
          // Auto-instrument route changes, fetch/XHR, INP, LCP, CLS, FCP, TTFB.
          enableInp: true,
          enableLongTask: true,
          enableLongAnimationFrame: true,
          // Mark idle transactions complete after 3s of no spans (default 1s
          // is too aggressive and truncates real user flows on slow networks).
          idleTimeout: 3000,
        }),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: true,
        }),
        // Only forward console.error/warn — debug/log/info create huge noise.
        Sentry.captureConsoleIntegration({ levels: ["error", "warn"] }),
        Sentry.httpClientIntegration(),
        Sentry.extraErrorDataIntegration({ depth: 4 }),
      ],
      // Sample rates — production is conservative; non-prod gets full visibility.
      tracesSampleRate: env === "production" ? 0.2 : 1.0,
      // Profiles are sampled relative to traces. 0.5 * 0.2 = 10% of prod sessions
      // get a CPU profile, plenty for hotspot analysis without quota burn.
      profilesSampleRate: env === "production" ? 0.5 : 1.0,
      // Drop low-value transactions (analytics beacons, sentry itself, etc.)
      // before they consume the traces quota.
      tracesSampler: (ctx) => {
        const name = ctx.name ?? "";
        if (/google-analytics|googletagmanager|tiktok|mixpanel|sentry\.io/.test(name)) return 0;
        // Boost sampling for revenue-critical paths so we always see them.
        if (/\/(checkout|verify-profile|reservation|admin)/.test(name)) {
          return env === "production" ? 1.0 : 1.0;
        }
        return env === "production" ? 0.2 : 1.0;
      },
      // NOTE: We previously dropped low-value spans (vendor URLs, sub-100ms
      // resource spans, browser mark/measure) via `beforeSendSpan` returning
      // null. That API is deprecated in @sentry/react v8 and removed in v9.
      // Vendor noise is instead suppressed via `denyUrls`, `tracePropagationTargets`,
      // and `tracesSampler` (which already filters analytics transactions). The
      // small uptick in resource-span volume is well within our prod quota at
      // tracesSampleRate=0.2.
      replaysSessionSampleRate: env === "production" ? 0.05 : 0.25,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: false,
      attachStacktrace: true,
      autoSessionTracking: true,
      normalizeDepth: 6,
      maxBreadcrumbs: 100,
      ignoreErrors: [
        "AbortError",
        "signal is aborted without reason",
        "Failed to register a ServiceWorker",
        "ResizeObserver loop limit exceeded",
        "Non-Error promise rejection captured",
      ],
      denyUrls: DENY_URLS,
      tracePropagationTargets: [
        "localhost",
        /^\//,
        // Anchored: an unanchored /getamorette\.com/ also matched third-party
        // URLs whose *query string* contains the domain (e.g. the Overrank
        // blog API's ?site=getamorette.com), which injected sentry-trace
        // headers into a request that rejects them via CORS preflight.
        /^https:\/\/(www\.)?getamorette\.com\//,
        /supabase\.co/,
      ],
      beforeSend(event, hint) {
        if (import.meta.env["DEV"]) return null;
        if (shouldIgnoreEvent(event, hint)) return null;
        // Group similar issues by stripping volatile data from titles.
        if (event.exception?.values?.[0]?.value) {
          event.exception.values[0].value = event.exception.values[0].value
            .replace(/0x[a-f0-9]+/gi, "0xHEX")
            .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<uuid>")
            .replace(/\b\d{6,}\b/g, "<num>");
        }
        return event;
      },
      beforeBreadcrumb(breadcrumb) {
        // Drop console.debug/log/info breadcrumbs — keep warn/error only.
        if (breadcrumb.category === "console" && !["warn", "error"].includes(breadcrumb.level ?? "")) {
          return null;
        }
        // Drop noisy fetch breadcrumbs to analytics endpoints.
        if (breadcrumb.category === "fetch" && typeof breadcrumb.data?.["url"] === "string") {
          const url = breadcrumb.data["url"] as string;
          if (/google-analytics|googletagmanager|tiktok|mixpanel|sentry\.io/.test(url)) {
            return null;
          }
        }
        return breadcrumb;
      },
    });
  })().catch((err) => {
    console.warn("[Sentry] lazy init failed:", err);
  });
  return initPromise;
};

/** Identify the current authenticated user to Sentry events. Safe to call before Sentry boots. */
export const setSentryUser = async (user: { id: string; email?: string } | null) => {
  await initPromise;
  if (!SentryRef) return;
  if (user) {
    SentryRef.setUser({ id: user.id, ...(user.email ? { email: user.email } : {}) });
  } else {
    SentryRef.setUser(null);
  }
};

/**
 * Wrap a critical user flow in a Sentry span. Auto-tags status, captures
 * thrown errors, and produces a measurable transaction in Performance.
 *
 * Usage:
 *   await traceFlow("auth.login", { email_domain: "gmail.com" }, async () => {
 *     return supabase.auth.signInWithPassword({ email, password });
 *   });
 */
export const traceFlow = async <T>(
  name: string,
  attributes: Record<string, string | number | boolean> | undefined,
  fn: () => Promise<T>,
): Promise<T> => {
  await initPromise;
  if (!SentryRef) return fn();
  return SentryRef.startSpan(
    { name, op: name.split(".")[0] || "flow", attributes: { flow: name, ...(attributes ?? {}) } },
    async (span) => {
      try {
        const result = await fn();
        span?.setStatus?.({ code: 1 }); // OK
        return result;
      } catch (err) {
        span?.setStatus?.({ code: 2, message: (err as Error)?.message ?? "error" });
        SentryRef!.withScope((scope) => {
          scope.setTag("flow", name);
          if (attributes) {
            Object.entries(attributes).forEach(([k, v]) => scope.setTag(k, String(v)));
          }
          SentryRef!.captureException(err);
        });
        throw err;
      }
    },
  );
};

/** Add a breadcrumb for flow milestones (e.g. "checkout.session.created"). */
export const addFlowBreadcrumb = (
  flow: string,
  message: string,
  data?: Record<string, unknown>,
) => {
  if (!SentryRef) return;
  SentryRef.addBreadcrumb({
    category: `flow.${flow}`,
    message,
    level: "info",
    ...(data ? { data } : {}),
  });
};

/** Manually capture a domain exception with a tag (e.g. flow:checkout). */
export const captureDomainException = async (
  error: unknown,
  context: { flow: string; extra?: Record<string, unknown>; tags?: Record<string, string> },
) => {
  await initPromise;
  if (!SentryRef) return;
  SentryRef.withScope((scope) => {
    scope.setTag("flow", context.flow);
    if (context.tags) {
      Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v));
    }
    if (context.extra) {
      Object.entries(context.extra).forEach(([k, v]) => scope.setExtra(k, v));
    }
    SentryRef!.captureException(error);
  });
};
